import reactUpdate from 'immutability-helper';

import {
  ChemistryReactionRole,
  CHEMISTRY_REACTION_ROLE_DISPLAY_NAME_TO_ENUM,
} from 'notebook/integrations/chemistry/ChemistryStructuredTableConstants';
import {
  StoichiometryStructuredTable,
  StoichiometryTableRow,
} from 'notebook/integrations/chemistry/StoichiometryStructuredTableAdapter';
import {
  StructuredTableColumnGroups,
  StructuredTableColumn,
} from 'notebook/structured-tables/StructuredTableConstants';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {EvaluatedCell} from 'notebook/tables/excess/EvaluationConstants';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';

export function getMoleculeIdFromEvaluatedCell(cell: EvaluatedCell): string | null {
  if (cell.value?.type === BaseTypedValueType.BIOENTITY && cell.value.bioentitySummary?.id) {
    return cell.value.bioentitySummary.id;
  }
  return null;
}

export function getRoleFromEvaluatedCell(cell: EvaluatedCell): ChemistryReactionRole | null {
  if (cell.value?.type === BaseTypedValueType.STRING && cell.value.rawValue) {
    if (CHEMISTRY_REACTION_ROLE_DISPLAY_NAME_TO_ENUM[cell.value.rawValue]) {
      return CHEMISTRY_REACTION_ROLE_DISPLAY_NAME_TO_ENUM[cell.value.rawValue]!;
    }
  }
  return null;
}

export function getColumnIndices(columns: Array<StructuredTableColumn>): {
  compoundColumnIndex: number;
  roleColumnIndex: number;
} {
  const compoundColumnIndex = columns.findIndex(
    (col) => col.columnGroup === StructuredTableColumnGroups.CHEMISTRY_MOLECULE
  );
  const roleColumnIndex = columns.findIndex(
    (col) => col.columnGroup === StructuredTableColumnGroups.CHEMISTRY_REACTION_ROLE
  );

  if (compoundColumnIndex === -1 || roleColumnIndex === -1) {
    throw new Error('Could not find required columns');
  }
  return {compoundColumnIndex, roleColumnIndex};
}

function getCompoundCellTypedValue(cell: Cell): TypedValue | null {
  return cell.typedValue ?? null;
}

function shouldClearDrawnMolfile(row: StoichiometryTableRow, compoundColumnIndex: number): boolean {
  const metadata = row.metadata;
  if (metadata?.drawnMolfile == null) {
    return false;
  }

  const cellTypedValue = getCompoundCellTypedValue(row.cells[compoundColumnIndex]);
  const moleculeId =
    cellTypedValue?.type === BaseTypedValueType.BIOENTITY
      ? (cellTypedValue.bioentitySummary?.id ?? null)
      : null;

  if (metadata.drawnMolfileMoleculeId != null) {
    return moleculeId !== metadata.drawnMolfileMoleculeId;
  }

  const canonicalSmiles = metadata.canonicalSmiles;
  if (canonicalSmiles != null) {
    if (cellTypedValue?.type === BaseTypedValueType.STRING) {
      return cellTypedValue.rawValue !== canonicalSmiles;
    }
    return true;
  }

  return false;
}

type EditorMetadataField = 'drawnMolfile' | 'drawnMolfileMoleculeId' | 'canonicalSmiles';

function getEditorMetadataFieldsToClear(
  row: StoichiometryTableRow,
  compoundColumnIndex: number
): Array<EditorMetadataField> {
  const metadata = row.metadata;
  if (metadata == null) {
    return [];
  }
  const hasDrawnMolfile = metadata.drawnMolfile != null;
  const hasCanonicalSmiles = metadata.canonicalSmiles != null;
  if (!hasDrawnMolfile && !hasCanonicalSmiles) {
    return [];
  }

  const cellTypedValue = getCompoundCellTypedValue(row.cells[compoundColumnIndex]);
  const moleculeId =
    cellTypedValue?.type === BaseTypedValueType.BIOENTITY
      ? (cellTypedValue.bioentitySummary?.id ?? null)
      : null;
  const cellSmiles = cellTypedValue?.type === BaseTypedValueType.STRING ? cellTypedValue.rawValue : null;
  const isEmpty =
    cellTypedValue == null || (cellTypedValue.type === BaseTypedValueType.STRING && cellSmiles === '');

  const fieldsToClear: Array<EditorMetadataField> = [];

  if (hasCanonicalSmiles && moleculeId == null) {
    if (isEmpty || cellSmiles !== metadata.canonicalSmiles) {
      fieldsToClear.push('canonicalSmiles');
    }
  }

  if (hasDrawnMolfile && shouldClearDrawnMolfile(row, compoundColumnIndex)) {
    fieldsToClear.push('drawnMolfile');
    if (metadata.drawnMolfileMoleculeId != null) {
      fieldsToClear.push('drawnMolfileMoleculeId');
    }
  }

  return fieldsToClear;
}

export function reconcileCompoundRowMetadata(
  structuredTable: StoichiometryStructuredTable
): StoichiometryStructuredTable {
  const {compoundColumnIndex} = getColumnIndices(structuredTable.columns);
  let rowsUpdate: Record<number, object> | null = null;

  structuredTable.rows.forEach((row, rowIndex) => {
    const fieldsToClear = getEditorMetadataFieldsToClear(row, compoundColumnIndex);
    if (fieldsToClear.length === 0) {
      return;
    }
    if (rowsUpdate == null) {
      rowsUpdate = {};
    }
    rowsUpdate[rowIndex] = {
      metadata: {$unset: fieldsToClear},
    };
  });

  if (rowsUpdate == null) {
    return structuredTable;
  }
  return reactUpdate(structuredTable, {rows: rowsUpdate});
}
